import { MouseEvent, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { allConditionTypes } from "common/types/condition-type";
import type { Context } from "common/types/context";
import { SchedulerFilter } from "common/types/scheduler-filters";
import { CancelButton } from "common/ui/buttons";
import { classNames } from "common/utils/jsx";
import { Modal } from "common/widgets/modal";
import type { ValueProps } from "common/with-value-for";
import { ConfigurationForm } from "./form";
import { isSchedulerFilterValid } from "./functions";

interface PropTypes extends ValueProps<SchedulerFilter> {
  context: Context;
}

export function EditConfiguration({
  context,
  value,
  onChange,
}: Readonly<PropTypes>) {
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<SchedulerFilter | undefined>(value);

  useEffect(() => {
    setFilter(value);
  }, [value]);

  const onCancelPopup = () => {
    unstable_batchedUpdates(() => {
      setFilter(value);
      setShowModal(false);
    });
  };

  const onSavePopup = () => {
    setShowModal(false);
    onChange(filter);
  };

  const onShowPopup = (e: MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const onFilterChange = (value: SchedulerFilter) => {
    setFilter(value);
  };

  const isConfigValid = isSchedulerFilterValid(context, filter);

  const configIcon = isConfigValid ? (
    <i className="fa fa-cog" />
  ) : (
    <i className="fa fa-warning" role="img" aria-label="Invalid Filter" />
  );
  const tooltip =
    !showModal && !isConfigValid
      ? _(
          "The applied filter is invalid. Please go to the filter configuration to fix it.",
        )
      : undefined;

  const className = classNames([
    "x-filter-action-button x-btn-filter-popup",
    !isConfigValid ? "x-filter-action-warning" : undefined,
  ]);

  return (
    <>
      <CancelButton
        className={classNames(["x-filter-action-button", className])}
        onClick={onShowPopup}
        disabled={false}
        title={_("Configure")}
        tooltip={tooltip}
        tooltipVariant={tooltip ? "error" : undefined}
      >
        {configIcon}
      </CancelButton>
      {showModal && (
        <Modal
          className="x-scheduler-work-orders-modal"
          content={
            <ConfigurationForm
              context={context}
              conditionTypes={allConditionTypes}
              value={filter}
              onChange={onFilterChange}
            />
          }
          size="large"
          title={_("Work Orders Configuration")}
          okLabel={_("Apply")}
          onOk={isConfigValid ? onSavePopup : undefined}
          onCancel={onCancelPopup}
        />
      )}
    </>
  );
}
